import React, { forwardRef, useRef, useLayoutEffect, useState } from 'react';
import { classNames, Title, Bold } from '@panwds/react-ui';
import { useSpring, config, animated } from '@react-spring/web';
import { metricsHelper } from '../../utils/metricsHelper.esm.js';
import { useNavigationContext } from '../NavigationContext/NavigationContext.esm.js';

const Submenu = forwardRef(({ children, id, otherWasOpen, isOpen, title }, ref) => {
    const el = useRef(null);
    // This state and the logic below is detecting if there is overflow or not.
    // Based on this we are adding gradient in the styles after.
    const [isOverflowing, setIsOverflowing] = React.useState(false);
    useLayoutEffect(() => {
        function updateSize() {
            setIsOverflowing(checkOverflow(el.current));
        }
        window.addEventListener("resize", updateSize);
        updateSize();
        return () => window.removeEventListener("resize", updateSize);
    }, []);
    function checkOverflow(el) {
        if (el === undefined || el === null) {
            return false;
        }
        const isOverflowing = el.clientHeight < el.scrollHeight;
        return isOverflowing;
    }
    const [expandedIsAnimating, setExpandingIsAnimating] = useState(false);
    const [collapsedIsAnimating, setCollapsedIsAnimating] = useState(false);
    const { collapsed } = useNavigationContext();
    const collapsedStyles = useSpring({
        immediate: otherWasOpen,
        onStart: 
        /* istanbul ignore next - animations are skipped in tests */
        () => setCollapsedIsAnimating(true),
        config: { ...config.stiff, clamp: true },
        to: { width: isOpen ? "14rem" : "0rem", translateX: "4rem" },
        onRest: 
        /* istanbul ignore next - animations are skipped in tests */
        () => setCollapsedIsAnimating(false),
    });
    const expandedStyles = useSpring({
        immediate: otherWasOpen,
        onStart: 
        /* istanbul ignore next - animations are skipped in tests */
        () => setExpandingIsAnimating(true),
        config: { ...config.stiff, clamp: true },
        to: { translateX: isOpen ? "0%" : "100%", width: "14rem" },
        onRest: 
        /* istanbul ignore next - animations are skipped in tests */
        () => setExpandingIsAnimating(false),
    });
    const normalizedId = metricsHelper(id);
    return (React.createElement(animated.nav, { style: collapsed ? collapsedStyles : expandedStyles, className: classNames("tw-absolute tw-box-border tw-border-solid tw-border-t-0 tw-border-b-0 tw-border-r-0 tw-border-l tw-border-black tw-top-0 tw-h-full tw-bg-blue-steel-1000 dark:tw-bg-black dark:tw-border-solid dark:tw-border-gray-950", "tw-overflow-x-hidden tw-overflow-y-auto tw-scrollbar-default tw-scrollbar-no-track dark:tw-scrollbar-thumb-dark dark:tw-scrollbar-corner-dark tw-transform tw-z-10", 
        /* istanbul ignore next - animations are skipped in tests */
        (!collapsedIsAnimating || !expandedIsAnimating) &&
            !isOpen &&
            "tw-sr-only", collapsed ? "tw-left-0 tw-z-10" : "tw-right-0 tw-z-20", isOverflowing
            ? "tw-bg-gradient-to-b tw-from-blue-steel-1000 tw-to-blue-steel-900"
            : ""), ref: el || ref, "aria-label": title, "data-metrics": `navigation-${normalizedId}-subnav`, "data-testid": isOpen ? `navigation-${normalizedId}-subnav` : undefined, "aria-hidden": !isOpen ? true : false, hidden: !isOpen ? true : false },
        React.createElement("div", { className: "tw-w-56" },
            React.createElement(Title, { level: 3, size: "xxs", addClassName: "tw-text-dark-bg tw-box-border tw-px-2 tw-ml-2 tw-py-5 tw-border-solid tw-border-t-0 tw-border-b-0 tw-border-r-0 tw-border-l-0 tw-border-blue-steel-800 dark:tw-border-gray-950", truncate: true },
                React.createElement(Bold, null, title)),
            React.createElement("ul", { className: "tw-box-border tw-list-none tw-m-0 tw-p-0 tw-b-0" }, children))));
});
Submenu.displayName = "Submenu";

export { Submenu };
//# sourceMappingURL=Submenu.esm.js.map
